import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AccessMode,
  type AccessRuleItem,
  addEmailRule,
  deriveMode,
  emailRulesFrom,
  removeEmailRule,
  rulesForMode,
} from "../lib/access-rules";
import type { AppType, SalesforceOrg } from "../lib/api";
import { slugFromName } from "../lib/slug";
import { InfoTooltip } from "./InfoTooltip";

export interface FormState {
  slug: string;
  name: string;
  description: string;
  app_type: AppType;
  upstream_url: string;
  target_url: string;
  strip_prefix: boolean;
  category: string;
  visibility: "private" | "public";
  shareable: boolean;
  access_rules: AccessRuleItem[];
  image_ref: string;
  internal_port: string;
  salesforce_required: boolean;
  salesforce_org_alias: string | null;
}

export const EMPTY_FORM: FormState = {
  slug: "",
  name: "",
  description: "",
  app_type: "managed",
  upstream_url: "",
  target_url: "",
  strip_prefix: true,
  category: "",
  visibility: "public",
  shareable: false,
  access_rules: [{ type: "all-authenticated", value: null }],
  image_ref: "",
  internal_port: "",
  salesforce_required: false,
  salesforce_org_alias: null,
};

export function AppForm(props: {
  initial: FormState;
  lockType?: boolean;
  /** Überschrift über dem Formular; fällt auf submitLabel zurück. */
  title?: string;
  sfOrgs?: SalesforceOrg[];
  /** #8: Gast-Share-Checkbox nur im Showroom-Modus zeigen (sonst wirkungslos). */
  sharingEnabled?: boolean;
  onCancel: () => void;
  onSubmit: (form: FormState) => Promise<void>;
  submitLabel: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(props.initial);
  const [busy, setBusy] = useState(false);
  const [slugDirty, setSlugDirty] = useState(props.initial.slug.length > 0);

  useEffect(() => {
    setForm(props.initial);
    setSlugDirty(props.initial.slug.length > 0);
  }, [props.initial]);

  const update = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));
  const onNameChange = (value: string): void => {
    setForm((f) => ({
      ...f,
      name: value,
      ...(slugDirty ? {} : { slug: slugFromName(value) }),
    }));
  };
  const onSlugChange = (value: string): void => {
    update({ slug: value });
    setSlugDirty(true);
  };
  const isExternal = form.app_type === "external";
  const isManaged = form.app_type === "managed";

  return (
    <form
      className="admin__form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await props.onSubmit(form);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h3>{props.title ?? props.submitLabel}</h3>
      <p className="form__req-note">{t("common.requiredHint")}</p>
      <div className="admin__field">
        <label htmlFor="app_type">{t("admin.col.appType")}</label>
        <select
          id="app_type"
          value={form.app_type}
          disabled={props.lockType}
          onChange={(e) => update({ app_type: e.target.value as AppType })}
        >
          <option value="managed">{t("admin.appType.managed")}</option>
          <option value="proxy">{t("admin.appType.proxy")}</option>
          <option value="external">{t("admin.appType.external")}</option>
        </select>
      </div>
      <div className="admin__field">
        <label htmlFor="name">
          {t("admin.col.name")}
          <span className="field-req" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="name"
          required
          value={form.name}
          onChange={(e) => onNameChange(e.target.value)}
        />
      </div>
      <div className="admin__field">
        <label htmlFor="slug">
          {t("admin.col.slug")}
          <span className="field-req" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="slug"
          required
          value={form.slug}
          onChange={(e) => onSlugChange(e.target.value)}
        />
        {!isExternal && form.slug ? (
          <small>
            {t("admin.col.path")}: <code>/{form.slug}/</code>
          </small>
        ) : null}
      </div>
      {isExternal ? (
        <div className="admin__field">
          <label htmlFor="target_url">
            {t("admin.col.target")}
            <span className="field-req" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="target_url"
            required
            placeholder="https://example.com/app"
            value={form.target_url}
            onChange={(e) => update({ target_url: e.target.value })}
          />
        </div>
      ) : null}
      {form.app_type === "proxy" ? (
        <div className="admin__field">
          <label htmlFor="upstream_url">
            {t("admin.col.upstream")}
            <span className="field-req" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="upstream_url"
            required
            placeholder="http://certgen:8080"
            value={form.upstream_url}
            onChange={(e) => update({ upstream_url: e.target.value })}
          />
        </div>
      ) : null}
      {isManaged && !props.lockType ? (
        <>
          <div className="admin__field">
            <label htmlFor="image_ref">
              {t("admin.runtime.field.imageRef")}
              <span className="field-req" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="image_ref"
              required
              placeholder="ghcr.io/tobiasharmes/certificate-generator:latest"
              value={form.image_ref}
              onChange={(e) => update({ image_ref: e.target.value })}
            />
          </div>
          <div className="admin__field">
            <span className="field-head">
              <label htmlFor="internal_port">{t("admin.runtime.field.internalPort")}</label>
              <InfoTooltip text={t("admin.runtime.field.internalPortHint")} />
            </span>
            <input
              id="internal_port"
              type="number"
              min={1}
              max={65535}
              placeholder={t("admin.runtime.field.internalPortPlaceholder")}
              value={form.internal_port}
              onChange={(e) => update({ internal_port: e.target.value })}
            />
          </div>
        </>
      ) : null}
      <div className="admin__field">
        <label htmlFor="description">{t("admin.col.description")}</label>
        <textarea
          id="description"
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
        />
      </div>
      <div className="admin__field">
        <label htmlFor="category">{t("admin.col.category")}</label>
        <input
          id="category"
          value={form.category}
          onChange={(e) => update({ category: e.target.value })}
        />
      </div>
      <div className="admin__field">
        <label htmlFor="visibility">{t("admin.col.visibility")}</label>
        <select
          id="visibility"
          value={form.visibility}
          onChange={(e) => update({ visibility: e.target.value as "private" | "public" })}
        >
          <option value="public">{t("admin.visibility.public")}</option>
          <option value="private">{t("admin.visibility.private")}</option>
        </select>
      </div>
      {isExternal ? null : (
        <div className="admin__field admin__field--inline">
          <label htmlFor="strip_prefix">
            <input
              id="strip_prefix"
              type="checkbox"
              checked={form.strip_prefix}
              onChange={(e) => update({ strip_prefix: e.target.checked })}
            />{" "}
            {t("admin.stripPrefix")}
          </label>
          <InfoTooltip text={t("admin.stripPrefixHint")} />
        </div>
      )}
      {isExternal || !props.sharingEnabled ? null : (
        <div className="admin__field admin__field--inline">
          <label htmlFor="shareable">
            <input
              id="shareable"
              type="checkbox"
              checked={form.shareable && !form.salesforce_required}
              disabled={form.salesforce_required}
              onChange={(e) => update({ shareable: e.target.checked })}
            />{" "}
            {t("admin.shareable")}
          </label>
          <InfoTooltip text={t("admin.shareableHint")} />
        </div>
      )}
      <AccessRulesEditor
        rules={form.access_rules}
        onChange={(rules) => update({ access_rules: rules })}
      />
      {!isExternal ? (
        <SalesforceBindingEditor
          orgs={props.sfOrgs ?? []}
          required={form.salesforce_required}
          orgAlias={form.salesforce_org_alias}
          onRequiredChange={(value) =>
            update({
              salesforce_required: value,
              salesforce_org_alias: value
                ? (form.salesforce_org_alias ?? props.sfOrgs?.[0]?.alias ?? null)
                : null,
            })
          }
          onAliasChange={(value) => update({ salesforce_org_alias: value })}
        />
      ) : null}
      {busy ? (
        <output className="admin__busy" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          {t("common.saving")}
        </output>
      ) : null}
      <div className="admin__form-actions">
        <button type="submit" className="primary" disabled={busy}>
          {props.submitLabel}
        </button>
        <button type="button" onClick={props.onCancel} disabled={busy}>
          {t("admin.cancel")}
        </button>
      </div>
    </form>
  );
}

export function AccessRulesEditor({
  rules,
  onChange,
}: {
  rules: AccessRuleItem[];
  onChange: (rules: AccessRuleItem[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const modeId = useId();
  const [emailInput, setEmailInput] = useState("");
  const [mode, setModeState] = useState<AccessMode>(() => deriveMode(rules));

  const prevRules = useRef(rules);
  useEffect(() => {
    if (prevRules.current === rules) return;
    prevRules.current = rules;
    if (rules.some((r) => r.type === "email")) {
      setModeState("email");
    } else if (rules.length > 0 && rules.every((r) => r.type === "all-authenticated")) {
      setModeState("all");
    }
  }, [rules]);

  const emailRules = emailRulesFrom(rules);

  const setMode = (newMode: AccessMode): void => {
    setModeState(newMode);
    onChange(rulesForMode(newMode));
  };

  const addEmail = (): void => {
    const next = addEmailRule(emailRules, emailInput);
    if (next === null) return;
    onChange(next);
    setEmailInput("");
  };

  const removeEmail = (email: string): void => {
    onChange(removeEmailRule(emailRules, email));
  };

  return (
    <div className="admin__field">
      <span className="field-head">
        <label htmlFor={modeId}>{t("admin.access.label")}</label>
        <InfoTooltip text={t("admin.access.emailHint")} />
      </span>
      <select id={modeId} value={mode} onChange={(e) => setMode(e.target.value as "all" | "email")}>
        <option value="all">{t("admin.access.allAuthenticated")}</option>
        <option value="email">{t("admin.access.emailOnly")}</option>
      </select>
      {mode === "email" && (
        <div className="admin__access-rules">
          <div className="admin__access-input">
            <input
              type="email"
              placeholder={t("admin.access.emailPlaceholder")}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addEmail();
                }
              }}
            />
            <button type="button" onClick={addEmail}>
              {t("admin.access.add")}
            </button>
          </div>
          {emailRules.length === 0 ? (
            <p className="admin__access-empty">{t("admin.access.noEmails")}</p>
          ) : (
            <ul className="admin__access-list">
              {emailRules.map((r) => (
                <li key={r.value} className="admin__access-tag">
                  <span>{r.value}</span>
                  <button type="button" onClick={() => r.value != null && removeEmail(r.value)}>
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function SalesforceBindingEditor({
  orgs,
  required,
  orgAlias,
  onRequiredChange,
  onAliasChange,
}: {
  orgs: SalesforceOrg[];
  required: boolean;
  orgAlias: string | null;
  onRequiredChange: (value: boolean) => void;
  onAliasChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const requiredId = useId();
  const orgId = useId();
  const enabledOrgs = orgs.filter((o) => o.enabled);
  const hasOrgs = enabledOrgs.length > 0;
  const referencesUnknownOrg =
    required && orgAlias !== null && !enabledOrgs.some((o) => o.alias === orgAlias);

  return (
    <div className="admin__field">
      <label htmlFor={requiredId}>{t("admin.appsSf.sectionLabel")}</label>
      {!hasOrgs ? (
        <small>{t("admin.appsSf.noOrgsHint")}</small>
      ) : (
        <>
          <label className="admin__field--inline">
            <input
              id={requiredId}
              type="checkbox"
              checked={required}
              onChange={(e) => onRequiredChange(e.target.checked)}
            />{" "}
            {t("admin.appsSf.requireCheckbox")}
          </label>
          {required ? (
            <>
              <span className="field-head">
                <label htmlFor={orgId}>
                  {t("admin.appsSf.selectOrg")}
                  <span className="field-req" aria-hidden="true">
                    *
                  </span>
                </label>
                <InfoTooltip text={t("admin.appsSf.hint")} />
              </span>
              <select
                id={orgId}
                value={orgAlias ?? ""}
                onChange={(e) => onAliasChange(e.target.value)}
                required
              >
                <option value="" disabled>
                  {t("admin.appsSf.selectOrg")}
                </option>
                {enabledOrgs.map((o) => (
                  <option key={o.alias} value={o.alias}>
                    {o.display_name} ({o.alias})
                  </option>
                ))}
              </select>
              {referencesUnknownOrg ? (
                <small className="launchpad__hint--error">
                  {t("admin.appsSf.unknownOrgWarn", { alias: orgAlias })}
                </small>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
